import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import type {
  ActionConstructor,
  AskResponse,
  TargetableModel,
} from '@cotera/client/v0/actions/framework/types';
import { z } from 'zod/v3';
import { Assert } from '@cotera/utilities';
import { DG } from '@cotera/sdk/core';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { err, ok } from 'neverthrow';
import { ModalPageView, Icon } from '@cotera/client/app/components';
import { useState } from 'react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Watchable } from '@cotera/client/v0/actions/framework';
import { COLUMN_ACTION_GROUP } from '../constants';
import { PublishGraphAction } from '@cotera/client/v0/graph/publish.action';
import { cn } from '@cotera/client/app/components/app/rte/lib/tiptap-utils';

type RunFromStartParams = {};

type RunFromStartModel = TargetableModel & {
  datasetName: string;
  currentNameInGraph: Watchable<string>;
  config: ReadonlyWatchable<DG.Graph.ColConfig | null>;
};

type ModelConstructor = new (...args: any[]) => RunFromStartModel;

/**
 * Waits for AutoPublishAction to complete publishing staged operations.
 *
 * When auto-publish is enabled, staging operations triggers the OPS_ADD_TO_STAGED event,
 * which causes AutoPublishAction to run asynchronously. Since we need the operations to be
 * published before proceeding (e.g., before running a snapshot), we must wait for the
 * auto-publish to complete rather than manually publishing (which would cause duplicate commits).
 *
 * This function polls hasUnpublishedOperations until it becomes false, indicating that
 * AutoPublishAction has completed and cleared the staged operations.
 */
async function waitForAutoPublishToComplete(
  graphModel: GraphModel
): Promise<ActionResult<{ t: string; message?: string }[]>> {
  const maxWaitTime = 10_000; // 10 seconds
  const pollInterval = 50; // 50ms
  const maxAttempts = maxWaitTime / pollInterval;
  let attempts = 0;

  while (
    graphModel.hasUnpublishedOperations.snapshot() &&
    attempts < maxAttempts
  ) {
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
    attempts++;
  }

  // If operations are still unpublished after waiting, something went wrong
  if (graphModel.hasUnpublishedOperations.snapshot()) {
    return err([
      {
        t: 'autoPublishTimeout',
        message: 'Auto-publish did not complete in time. Please try again.',
      },
    ]);
  }

  return ok({});
}

export const makeRunFromStartAction = (
  title: string,
  modelConstructor: ModelConstructor,
  snapshotActionConstructor: ActionConstructor<{}, any>
) => {
  return class extends BaseAction<RunFromStartParams, { t: string }[]> {
    title = title;
    inputSchema = z.object({});
    group = COLUMN_ACTION_GROUP;
    icon = 'clock-reset';
    discrete = true;
    description = 'Run the column from the beginning';

    constructor(
      private readonly modalManager: {
        push: (props: {
          view: () => React.ReactNode;
          onClose?: () => void;
        }) => void;
        pop: () => void;
      }
    ) {
      super();
    }

    applicable(context: ApplicableContext): boolean {
      const model = getTargetedModel(context, modelConstructor);
      return model !== null && model.config.snapshot() !== undefined;
    }

    async execute(
      _payload: RunFromStartParams,
      context: ExecuteContext
    ): Promise<ActionResult<{ t: string }[]>> {
      const model = getTargetedModel(context, modelConstructor);
      const graphModel = context.getInScopeModelOfType(GraphModel);

      Assert.assert(model !== null);
      Assert.assert(graphModel !== null);

      const config = model.config.snapshot();
      Assert.assert(config !== null);

      const replaceOp: DG.ReplaceColOp = DG.Operations.replaceColumn({
        relName: model.datasetName,
        name: model.currentNameInGraph.snapshot(),
        config: config as any,
      });

      const result = await graphModel.attemptToStageOperations([replaceOp]);

      if (result.isErr()) {
        return err([{ t: 'build', e: result.error }]);
      }

      // Explicitly publish the staged operations to ensure the column exists on the backend
      // before attempting to snapshot it. This prevents a race condition where auto-publish
      // might not complete before the snapshot action tries to run.
      // However, if auto-publish is enabled, AutoPublishAction will handle publishing
      // automatically via the OPS_ADD_TO_STAGED event, so we should wait for that instead.
      if (graphModel.hasUnpublishedOperations.snapshot()) {
        // If auto-publish is enabled, AutoPublishAction will handle publishing automatically
        // via the OPS_ADD_TO_STAGED event. We should NOT manually publish to avoid duplicate commits.
        // Instead, wait for the auto-publish to complete.
        if (!graphModel.autoPublish.snapshot()) {
          // Manual publish when auto-publish is disabled
          const publishResult = await context
            .getAction(PublishGraphAction)
            ?.askOrExecute({}, context);

          if (publishResult?.isErr()) {
            return err([
              {
                t: 'publish',
                message:
                  'Failed to publish changes: ' +
                  JSON.stringify(publishResult.error),
              },
            ]);
          }
        } else {
          // Auto-publish is enabled - wait for AutoPublishAction to complete
          const waitResult = await waitForAutoPublishToComplete(graphModel);
          if (waitResult.isErr()) {
            return waitResult;
          }
        }
      }

      // Now run the snapshot action
      const snapshotResult = await context
        .getAction(snapshotActionConstructor)
        ?.askOrExecute({}, context);

      if (snapshotResult?.isErr()) {
        return err([
          {
            t: 'snapshot',
            message:
              'Snapshot action failed: ' + JSON.stringify(snapshotResult.error),
          },
        ]);
      }

      return ok({});
    }

    shouldAsk(_: ApplicableContext): boolean {
      return true;
    }

    ask(
      _: RunFromStartParams,
      context: ExecuteContext
    ): AskResponse<RunFromStartParams> {
      const model = getTargetedModel(context, modelConstructor);

      Assert.assert(model !== null);

      return {
        view: ({ onRespond, onCancel }) => {
          this.modalManager.push({
            view: () => (
              <AskView
                onRespond={async (params) => {
                  // Call onRespond which will trigger execute() and await all work (publish + snapshot)
                  // This ensures askOrExecute doesn't resolve until all async work is complete
                  await onRespond(params);

                  // Close the modal after all work is complete
                  this.modalManager.pop();
                }}
                onCancel={async () => {
                  await onCancel();
                  this.modalManager.pop();
                }}
                model={model}
              />
            ),
            onClose: async () => {
              await onCancel();
            },
          });
        },
      };
    }
  };
};

export const AskView: React.FC<{
  onRespond: (params: RunFromStartParams) => Promise<void>;
  onCancel: () => void;
  model: RunFromStartModel;
}> = ({ onRespond, model, onCancel }) => {
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView
      title={`Run ${model.currentNameInGraph.snapshot()} From Start`}
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          This will restart the execution of this column from the beginning. Are
          you sure you want to continue?
        </p>

        <div className="w-full flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            disabled={loading}
            className={cn(loading && 'shimmer')}
            variant="default"
            onClick={async () => {
              setLoading(true);
              await onRespond({});
              setLoading(false);
            }}
          >
            <Icon icon={'play'}></Icon>
            {loading ? 'Running...' : 'Run From Start'}
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};

const getTargetedModel = (
  context: Pick<ExecuteContext, 'targetedModelOfType'>,
  modelConstructor: ModelConstructor
): RunFromStartModel | null => {
  const model = context.targetedModelOfType(modelConstructor);
  return model;
};
